import React, { useState } from 'react';
import config from './config';

const PatentDecoderPage = ({ user, onBack, setPage, onUsageUpdate }) => {
  const [step, setStep] = useState(1);
  const [patentNumber, setPatentNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [patentData, setPatentData] = useState(null);
  const [decodedAnalysis, setDecodedAnalysis] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handlePatentLookup = async () => {
    if (!patentNumber.trim()) {
      setError('Please enter a patent number');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      // Step 1: Fetch patent data using the correct endpoint
      const cleanNumber = patentNumber.trim().replace(/[^a-zA-Z0-9]/g, '');
      const response = await fetch(`${config.API_URL}/api/search/patent-number/${encodeURIComponent(cleanNumber)}`);
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        setPatentData(data.results[0]);
        setStep(2);
        
        // Step 2: Get AI analysis
        await decodePatent(data.results[0]);
      } else {
        setError('Patent not found in our database. Try a US patent number like 11856881 or 10123456.');
      }
    } catch (err) {
      setError('Error looking up patent. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const decodePatent = async (patent) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/api/decode-patent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patent_number: patent.patent_number,
          title: patent.title,
          abstract: patent.abstract || patent.summary,
          claims: patent.claims,
          token: token
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setDecodedAnalysis(data.analysis);
        setStep(3);
        if (onUsageUpdate) onUsageUpdate();
      } else {
        setError(data.error || 'Error analyzing patent');
      }
    } catch (err) {
      setError('Error analyzing patent. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetJourney = () => {
    setStep(1);
    setPatentNumber('');
    setPatentData(null);
    setDecodedAnalysis(null);
    setError('');
  };

  // Save report to database
  const saveReport = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please log in to save reports');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${config.API_URL}/api/saved-individual-reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          report_type: 'patent-decoder',
          title: patentData?.title || 'Patent Decoder Report',
          input_data: { patent_number: patentNumber, patent_data: patentData },
          results_data: decodedAnalysis,
          token: token
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        alert(data.error || 'Failed to save report');
      }
    } catch (err) {
      alert('Error saving report');
    } finally {
      setSaving(false);
    }
  };

  // Download as HTML
  const downloadHTML = () => {
    const analysis = decodedAnalysis?.analysis || decodedAnalysis || {};
    
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <title>Patent Decoder: ${patentData?.patent_number || patentNumber || 'Report'}</title>
  <style>
    body { font-family: Arial, sans-serif; max-width: 900px; margin: 0 auto; padding: 20px; line-height: 1.6; }
    h1 { color: #1e293b; border-bottom: 3px solid #0369a1; padding-bottom: 10px; }
    h2 { color: #0369a1; margin-top: 30px; }
    .patent-header { background: #f0f9ff; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #0369a1; }
    .patent-header h2 { margin-top: 0; color: #0369a1; }
    .summary-box { background: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0; font-size: 16px; }
    .covers-section { background: #dcfce7; padding: 20px; border-radius: 8px; margin: 15px 0; }
    .covers-section h3 { color: #166534; margin-top: 0; }
    .covers-section li { color: #166534; margin: 8px 0; }
    .not-covers-section { background: #fee2e2; padding: 20px; border-radius: 8px; margin: 15px 0; }
    .not-covers-section h3 { color: #991b1b; margin-top: 0; }
    .not-covers-section li { color: #991b1b; margin: 8px 0; }
    .claims-section { background: #f8fafc; padding: 20px; border-radius: 8px; margin: 15px 0; }
    .claim-card { background: white; border: 1px solid #e2e8f0; padding: 15px; border-radius: 8px; margin: 10px 0; }
    .claim-number { font-weight: bold; color: #0369a1; }
    .affect-section { background: #fef3c7; padding: 20px; border-radius: 8px; margin: 15px 0; border-left: 4px solid #d97706; }
    .affect-section h3 { color: #92400e; margin-top: 0; }
    .infringement-section { background: #f8fafc; padding: 20px; border-radius: 8px; margin: 15px 0; }
    .infringement-section li { margin: 8px 0; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; color: #64748b; font-size: 12px; }
  </style>
</head>
<body>
  <h1>Patent Decoder Report</h1>

  <div class="patent-header">
    <h2>${patentData?.patent_number || patentNumber || 'Patent'}</h2>
    <p><strong>${patentData?.title || 'Title not available'}</strong></p>
    <p><strong>Assignee:</strong> ${patentData?.assignee || 'Not specified'}</p>
    <p><strong>Filing Date:</strong> ${patentData?.filing_date || 'Not specified'}</p>
  </div>

  <h2>In Plain English</h2>
  <div class="summary-box">
    <p>${analysis?.plain_english_summary || 'No summary available'}</p>
  </div>

  <div class="covers-section">
    <h3>What This Patent Covers</h3>
    <ul>
      ${(analysis?.what_it_covers || []).map(item => `<li>✓ ${item}</li>`).join('') || '<li>No coverage information available</li>'}
    </ul>
  </div>

  <div class="not-covers-section">
    <h3>What It Does NOT Cover</h3>
    <ul>
      ${(analysis?.what_it_doesnt_cover || []).map(item => `<li>✗ ${item}</li>`).join('') || '<li>No exclusion information available</li>'}
    </ul>
  </div>

  ${analysis?.key_claims && analysis.key_claims.length > 0 ? `
    <h2>Key Claims Translated</h2>
    <div class="claims-section">
      ${analysis.key_claims.map(claim => `
        <div class="claim-card">
          <span class="claim-number">CLAIM ${claim.number}</span>
          <p>${claim.plain_english}</p>
        </div>
      `).join('')}
    </div>
  ` : ''}

  <div class="affect-section">
    <h3>Could This Affect You?</h3>
    <p>${analysis?.could_affect_you || 'No impact analysis available'}</p>
  </div>

  ${analysis?.infringement_requirements && analysis.infringement_requirements.length > 0 ? `
    <h2>To Potentially Infringe, You Would Need</h2>
    <div class="infringement-section">
      <ul>
        ${analysis.infringement_requirements.map(req => `<li>• ${req}</li>`).join('')}
      </ul>
    </div>
  ` : ''}

  <div class="footer">
    <p><strong>Disclaimer:</strong> This is an AI-generated analysis for informational purposes only. It is not legal advice. Consult a patent attorney for professional guidance.</p>
    <p>Generated by PatTrak - ${new Date().toLocaleDateString()}</p>
  </div>
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `PatTrak_Decoded_${(patentData?.patent_number || patentNumber || 'patent').replace(/[^a-z0-9]/gi, '_')}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Download as PDF
  const downloadPDF = async () => {
    if (!decodedAnalysis) return;
    const analysis = decodedAnalysis?.analysis || decodedAnalysis || {};
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <title>Patent Decoder: ${patentData?.patent_number || patentNumber || 'Report'}</title>
  <style>
    body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px; line-height: 1.6; }
    h1 { color: #1e293b; font-size: 24px; }
    h2 { color: #0369a1; font-size: 18px; border-bottom: 2px solid #0369a1; padding-bottom: 8px; margin-top: 30px; }
    .section { background: #f8fafc; padding: 20px; border-radius: 8px; margin: 15px 0; }
    .patent-header { background: #f0f9ff; padding: 15px; border-radius: 8px; margin: 15px 0; border-left: 4px solid #0369a1; }
    ul { margin: 10px 0; padding-left: 20px; }
    li { margin: 8px 0; }
    .covers { background: #dcfce7; padding: 15px; border-radius: 8px; margin: 15px 0; }
    .covers li { color: #166534; }
    .not-covers { background: #fee2e2; padding: 15px; border-radius: 8px; margin: 15px 0; }
    .not-covers li { color: #991b1b; }
    .claim { background: white; border: 1px solid #e2e8f0; padding: 12px; margin: 10px 0; border-radius: 6px; }
    .claim-num { font-weight: bold; color: #0369a1; }
    .warning { background: #fef3c7; padding: 15px; border-radius: 8px; margin: 15px 0; border-left: 4px solid #d97706; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; color: #64748b; font-size: 12px; }
  </style>
</head>
<body>
  <h1>Patent Decoder Report</h1>

  <div class="patent-header">
    <strong>${patentData?.patent_number || patentNumber || 'Patent'}</strong>
    <p>${patentData?.title || 'Title not available'}</p>
    <small>Assignee: ${patentData?.assignee || 'Not specified'}</small>
  </div>

  <h2>In Plain English</h2>
  <div class="section">${analysis?.plain_english_summary || ''}</div>

  <h2>What This Patent Covers</h2>
  <div class="covers"><ul>${(analysis?.what_it_covers || []).map(item => '<li>✓ ' + item + '</li>').join('') || '<li>Not available</li>'}</ul></div>

  <h2>What It Does NOT Cover</h2>
  <div class="not-covers"><ul>${(analysis?.what_it_doesnt_cover || []).map(item => '<li>✗ ' + item + '</li>').join('') || '<li>Not available</li>'}</ul></div>

  ${analysis?.key_claims && analysis.key_claims.length > 0 ? '<h2>Key Claims Translated</h2>' + analysis.key_claims.map(c => '<div class="claim"><span class="claim-num">CLAIM ' + c.number + '</span><p>' + c.plain_english + '</p></div>').join('') : ''}

  <div class="warning">
    <h3>Could This Affect You?</h3>
    <p>${analysis?.could_affect_you || ''}</p>
  </div>

  ${analysis?.infringement_requirements && analysis.infringement_requirements.length > 0 ? '<h2>To Potentially Infringe, You Would Need</h2><div class="section"><ul>' + analysis.infringement_requirements.map(r => '<li>' + r + '</li>').join('') + '</ul></div>' : ''}

  <div class="footer">Generated by PatTrak - Patent Intelligence Made Simple<br/>This is not legal advice.</div>
</body>
</html>`;
    try {
      const response = await fetch(`${config.API_URL}/api/generate-invention-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          report_html: htmlContent,
          title: patentData?.patent_number || patentNumber || 'Patent_Decoder'
        })
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `PatTrak_Decoded_${(patentData?.patent_number || patentNumber || 'patent').replace(/[^a-z0-9]/gi, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        alert('Failed to generate PDF');
      }
    } catch (error) {
      console.error('PDF error:', error);
      alert('Failed to generate PDF');
    }
  };

  // Tier gate - only Premium and Pro users can access
  if (!user || user.tier === 'free') {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
        <header style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0369a1', fontSize: '14px', fontWeight: '500' }}>
            ← Back
          </button>
        </header>
        
        <main style={{ maxWidth: '600px', margin: '80px auto', padding: '0 16px', textAlign: 'center' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '48px 32px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: '64px', marginBottom: '24px' }}>🔒</div>
            <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', marginBottom: '12px' }}>
              Patent Decoder
            </h1>
            <p style={{ color: '#64748b', fontSize: '16px', lineHeight: '1.6', marginBottom: '32px' }}>
              Get AI-powered explanations of complex patent documents in plain English.
            </p>
            
            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '12px', padding: '20px', marginBottom: '32px' }}>
              <p style={{ color: '#0369a1', fontSize: '14px', margin: 0 }}>
                {!user 
                  ? 'Please register or log in to access this tool.'
                  : 'This tool requires a Premium or Pro subscription.'
                }
              </p>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '280px', margin: '0 auto' }}>
              {!user ? (
                <>
                  <button onClick={() => setPage('register')} style={{ padding: '14px 28px', background: '#0369a1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '16px' }}>
                    Register Free
                  </button>
                  <button onClick={() => setPage('login')} style={{ padding: '14px 28px', background: 'white', color: '#0369a1', border: '1px solid #0369a1', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '16px' }}>
                    Log In
                  </button>
                </>
              ) : (
                <button onClick={() => setPage('pricing')} style={{ padding: '14px 28px', background: '#0369a1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '16px' }}>
                  Upgrade to Premium
                </button>
              )}
              <button onClick={onBack} style={{ padding: '10px', background: 'none', color: '#64748b', border: 'none', cursor: 'pointer', fontSize: '14px' }}>
                Maybe Later
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            color: '#0369a1',
            cursor: 'pointer',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: 0,
            marginBottom: '16px'
          }}
        >
          ← Back to Search
        </button>
        
        <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', margin: 0 }}>
          📖 Patent Decoder
        </h1>
        <p style={{ color: '#64748b', marginTop: '8px', fontSize: '16px' }}>
          Understand any patent in plain English
        </p>
      </div>

      {/* Progress Steps */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        marginBottom: '32px',
        padding: '16px',
        background: '#f8fafc',
        borderRadius: '8px'
      }}>
        {[
          { num: 1, label: 'Enter Patent' },
          { num: 2, label: 'Analyzing' },
          { num: 3, label: 'Decoded' }
        ].map((s, idx) => (
          <React.Fragment key={s.num}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              opacity: step >= s.num ? 1 : 0.4
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: step > s.num ? '#16a34a' : step === s.num ? '#0369a1' : '#e2e8f0',
                color: step >= s.num ? 'white' : '#64748b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                {step > s.num ? '✓' : s.num}
              </div>
              <span style={{ 
                fontSize: '14px', 
                fontWeight: step === s.num ? '600' : '400',
                color: step === s.num ? '#0369a1' : '#64748b'
              }}>
                {s.label}
              </span>
            </div>
            {idx < 2 && (
              <div style={{ 
                flex: 1, 
                height: '2px', 
                background: step > s.num ? '#16a34a' : '#e2e8f0',
                margin: '0 16px'
              }} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Enter Patent Number */}
      {step === 1 && (
        <div style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '32px'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
            Enter a Patent Number
          </h2>
          <p style={{ color: '#64748b', marginBottom: '24px' }}>
            I'll translate it into plain English and explain what it actually protects.
          </p>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
              Patent Number
            </label>
            <input
              type="text"
              value={patentNumber}
              onChange={(e) => setPatentNumber(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handlePatentLookup()}
              placeholder="e.g., US10123456, US2020/0123456"
              style={{
                width: '100%',
                padding: '14px 16px',
                fontSize: '16px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#0369a1'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
            <p style={{ fontSize: '13px', color: '#94a3b8', marginTop: '8px' }}>
              Examples: US10123456B2, US11234567, EP1234567A1
            </p>
          </div>

          {error && (
            <div style={{
              padding: '12px 16px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#dc2626',
              marginBottom: '24px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          <button
            onClick={handlePatentLookup}
            disabled={loading || !patentNumber.trim()}
            style={{
              width: '100%',
              padding: '14px 24px',
              fontSize: '16px',
              fontWeight: '600',
              color: 'white',
              background: loading || !patentNumber.trim() ? '#94a3b8' : '#0369a1',
              border: 'none',
              borderRadius: '8px',
              cursor: loading || !patentNumber.trim() ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Looking up patent...' : 'Decode This Patent →'}
          </button>

          {/* Learning moment */}
          <div style={{
            marginTop: '32px',
            padding: '16px',
            background: '#f0fdf4',
            borderRadius: '8px',
            border: '1px solid #bbf7d0'
          }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#166534', marginBottom: '8px' }}>
              💡 What you'll learn
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#15803d' }}>
              <li>What the patent actually protects (and doesn't)</li>
              <li>Plain-English translation of technical claims</li>
              <li>Whether it could affect your work</li>
            </ul>
          </div>
        </div>
      )}

      {/* Step 2: Analyzing */}
      {step === 2 && loading && (
        <div style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '48px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
            Analyzing Patent...
          </h2>
          <p style={{ color: '#64748b', marginBottom: '24px' }}>
            Reading the claims and translating to plain English
          </p>
          <div style={{
            width: '200px',
            height: '8px',
            background: '#e2e8f0',
            borderRadius: '4px',
            margin: '0 auto',
            overflow: 'hidden'
          }}>
            <div style={{
              width: '60%',
              height: '100%',
              background: '#0369a1',
              borderRadius: '4px',
              animation: 'pulse 1.5s infinite'
            }} />
          </div>
        </div>
      )}

      {/* Step 3: Results */}
      {step === 3 && decodedAnalysis && (
        <div>
          {/* Patent Summary Card */}
          <div style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>
                  {patentData.patent_number}
                </div>
                <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', margin: 0 }}>
                  {patentData.title}
                </h2>
                <div style={{ fontSize: '14px', color: '#64748b', marginTop: '8px' }}>
                  {patentData.assignee}{patentData.filing_date ? ` • Filed: ${patentData.filing_date}` : ''}
                </div>
              </div>
              <span style={{
                padding: '4px 12px',
                background: patentData.status === 'Live' ? '#dcfce7' : patentData.status === 'Expired' ? '#fee2e2' : '#fef3c7',
                color: patentData.status === 'Live' ? '#166534' : patentData.status === 'Expired' ? '#dc2626' : '#92400e',
                borderRadius: '16px',
                fontSize: '13px',
                fontWeight: '500'
              }}>
                {patentData.status || 'Live'}
              </span>
            </div>
          </div>

          {/* Plain English Summary */}
          <div style={{
            background: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#0369a1', marginBottom: '12px' }}>
              💬 In Plain English
            </h3>
            <p style={{ fontSize: '16px', color: '#1e293b', lineHeight: '1.6', margin: 0 }}>
              {decodedAnalysis.plain_english_summary}
            </p>
          </div>

          {/* What It Protects */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '24px',
            marginBottom: '24px'
          }}>
            <div style={{
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '12px',
              padding: '24px'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#166534', marginBottom: '12px' }}>
                ✓ What This Patent Covers
              </h3>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#15803d', fontSize: '14px', lineHeight: '1.8' }}>
                {decodedAnalysis.what_it_covers?.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>

            <div style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '12px',
              padding: '24px'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#dc2626', marginBottom: '12px' }}>
                ✗ What It Does NOT Cover
              </h3>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#b91c1c', fontSize: '14px', lineHeight: '1.8' }}>
                {decodedAnalysis.what_it_doesnt_cover?.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Key Claims */}
          <div style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
              🔑 Key Claims Translated
            </h3>
            {decodedAnalysis.key_claims?.map((claim, idx) => (
              <div key={idx} style={{
                padding: '16px',
                background: '#f8fafc',
                borderRadius: '8px',
                marginBottom: idx < decodedAnalysis.key_claims.length - 1 ? '12px' : 0
              }}>
                <div style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>
                  CLAIM {claim.number}
                </div>
                <div style={{ fontSize: '14px', color: '#1e293b', lineHeight: '1.6' }}>
                  {claim.plain_english}
                </div>
              </div>
            ))}
          </div>

          {/* Could This Affect You? */}
          <div style={{
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#92400e', marginBottom: '12px' }}>
              ⚠️ Could This Affect You?
            </h3>
            <p style={{ fontSize: '14px', color: '#78350f', lineHeight: '1.6', marginBottom: '16px' }}>
              {decodedAnalysis.could_affect_you}
            </p>
            <div style={{ fontSize: '13px', color: '#92400e' }}>
              <strong>To potentially infringe this patent, you would need to:</strong>
              <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                {decodedAnalysis.infringement_requirements?.map((req, idx) => (
                  <li key={idx}>{req}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={resetJourney}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#0369a1',
                background: 'white',
                border: '2px solid #0369a1',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Decode Another Patent
            </button>
            <button
              onClick={saveReport}
              disabled={saving}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                color: 'white',
                background: saveSuccess ? '#16a34a' : '#0369a1',
                border: 'none',
                borderRadius: '8px',
                cursor: saving ? 'not-allowed' : 'pointer'
              }}
            >
              {saving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Report'}
            </button>
            <button
              onClick={downloadHTML}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                background: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Download HTML
            </button>
            <button
              onClick={downloadPDF}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                color: 'white',
                background: '#0369a1',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Download PDF
            </button>
          </div>

          {/* What You Learned */}
          <div style={{
            marginTop: '32px',
            padding: '16px',
            background: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
              📚 What you learned
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#64748b' }}>
              <li>How to read patent claims and understand their scope</li>
              <li>The difference between what a patent covers vs. doesn't cover</li>
              <li>How to assess if a patent might affect your work</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatentDecoderPage;